"use client";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { TrendingUp } from "lucide-react";

/**
 * 일자별 정답률 변화 LineChart 본문 (최근 30일) — 제목 + 차트 + 범례.
 *
 * 오답노트 통계 뷰(`ErrorNoteStatsView`)와 `/my` 대시보드가 공용으로 쓴다(JCG-2073). 카드
 * 래퍼는 호출자가 씌운다(통계 뷰는 `<Card>`, 대시보드는 대시보드 카드). `accuracyRate`/`date`만
 * 사용하므로 prop 타입을 그 두 필드로 좁혀 `ErrorNoteDailyStats`와 분리(structural 호환).
 * 데이터 포인트가 2개 미만이면 추세가 무의미하므로 렌더하지 않는다.
 */
export default function DailyAccuracyChart({
  dailyStats,
  showTitle = true,
}: {
  dailyStats: { date: string; accuracyRate: number }[];
  /** 차트 자체 제목("일자별 정답률 변화") 표시 여부. 호출자가 제목을 따로 둘 때 false(JCG-2073 대시보드). */
  showTitle?: boolean;
}) {
  if (dailyStats.length <= 1) return null;

  return (
    <div className="space-y-4">
      {showTitle && (
        <div className="flex items-center gap-2 flex-wrap">
          <TrendingUp className="h-5 w-5 text-gray-500" aria-hidden />
          <span className="text-lg font-semibold dark:text-white">
            일자별 정답률 변화
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            (최근 30일)
          </span>
        </div>
      )}
      <div className="h-[200px] md:h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={dailyStats}
            margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              className="stroke-gray-200 dark:stroke-gray-700"
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
              className="text-gray-500 dark:text-gray-400"
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => `${value}%`}
              className="text-gray-500 dark:text-gray-400"
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
              formatter={(value: number, name: string) => {
                if (name === "accuracyRate")
                  return [`${value.toFixed(1)}%`, "정답률"];
                return [value, name];
              }}
              labelFormatter={(label) => {
                const date = new Date(label);
                return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
              }}
            />
            <ReferenceLine y={60} stroke="#f59e0b" strokeDasharray="5 5" />
            <Line
              type="monotone"
              dataKey="accuracyRate"
              stroke="#22c55e"
              strokeWidth={2}
              dot={{ fill: "#22c55e", strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5, fill: "#16a34a" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex justify-center gap-4 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-green-500"></div>
          <span>정답률</span>
        </div>
        <div className="flex items-center gap-1">
          <div
            className="w-3 h-0.5 bg-amber-500"
            style={{ borderStyle: "dashed" }}
          ></div>
          <span>목표 60%</span>
        </div>
      </div>
    </div>
  );
}
